import { convertLegacyRouteRefs } from '@backstage/core-compat-api';
import {
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
} from '@backstage/core-plugin-api';
import { CreateAppRouteBinder } from '@backstage/frontend-app-api';

import { createResourceLinkExtension } from '@spotify/confidence-core-react';
import {
  AUDIENCE_FLAGS_CHANGE_EXTENSION,
  AUDIENCE_SEGMENT_CHANGE_EXTENSION,
  FLAGS_MODULE_SUPPRESS_EXTENSION,
  INITIATIVE_CHANGE_EXTENSION,
  LABELS_CHANGE_EXTENSION,
  OWNER_CHANGE_EXTENSION,
  PLANNING_CHANGE_EXTENSION,
  SAMPLE_SIZE_CHANGE_EXTENSION,
  STATE_CHANGE_EXTENSION,
  STATS_CHANGE_EXTENSION,
  STRING_CHANGE_EXTENSION,
  SURFACES_CHANGE_EXTENSION,
  WorkflowInstanceResourceDisplayName,
  WorkflowInstanceResourceLink,
} from '@spotify/plugin-confidence-workflows-react';

import { ExternalRoutes, Routes, rootRouteRef } from './routes';

export const workflowsPlugin = createPlugin({
  id: 'workflows',
  routes: {
    ...Routes,
  },
  externalRoutes: {
    ...ExternalRoutes,
  },
});

export const bindWorkflowsRoutes = (
  bind: CreateAppRouteBinder,
  extraRoutes?: Parameters<typeof convertLegacyRouteRefs>[0],
) => {
  bind(
    convertLegacyRouteRefs(workflowsPlugin.externalRoutes),
    convertLegacyRouteRefs({
      ...workflowsPlugin.routes,
      ...extraRoutes,
    }),
  );
};

export const ConfidenceWorkflowsPage = workflowsPlugin.provide(
  createRoutableExtension({
    name: 'ConfidenceWorkflowsPage',
    component: () =>
      import(
        /* webpackChunkName: "confidence-workflows", webpackPrefetch: 10 */ './Router'
      ).then(m => m.Router),
    mountPoint: rootRouteRef,
  }),
);

export const ConfidenceWorkflowsExtension = workflowsPlugin.provide(
  createComponentExtension({
    name: 'ConfidenceWorkflowsExtension',
    component: {
      lazy: () =>
        import(
          /* webpackChunkName: "confidence-workflows-extension", webpackPrefetch: 10 */ './Router'
        ).then(m => m.Router),
    },
  }),
);

export const WorkflowInstanceResourceLinkExtension = workflowsPlugin.provide(
  createResourceLinkExtension({
    id: 'workflow-instance-resource-link',
    predicate: ({ name }) => /^workflows\/[^/]+\/instances\//.test(name),
    DisplayName: WorkflowInstanceResourceDisplayName,
    icon: 'workflow',
    render: ({ name, text, className }) => (
      <WorkflowInstanceResourceLink
        name={name}
        text={text}
        className={className}
      />
    ),
  }),
);

// Change extensions

export const InitiativeChangeExtension = workflowsPlugin.provide(
  INITIATIVE_CHANGE_EXTENSION,
);

export const LabelsChangeExtension = workflowsPlugin.provide(
  LABELS_CHANGE_EXTENSION,
);

export const PlanningChangeExtension = workflowsPlugin.provide(
  PLANNING_CHANGE_EXTENSION,
);

export const SurfacesChangeExtension = workflowsPlugin.provide(
  SURFACES_CHANGE_EXTENSION,
);

export const WorkflowStringChangeExtension = workflowsPlugin.provide(
  STRING_CHANGE_EXTENSION,
);

export const WorkflowOwnerChangeExtension = workflowsPlugin.provide(
  OWNER_CHANGE_EXTENSION,
);

export const StatsChangeExtension = workflowsPlugin.provide(
  STATS_CHANGE_EXTENSION,
);

export const AudienceFlagsChangeExtension = workflowsPlugin.provide(
  AUDIENCE_FLAGS_CHANGE_EXTENSION,
);

export const FlagsModuleSuppressExtension = workflowsPlugin.provide(
  FLAGS_MODULE_SUPPRESS_EXTENSION,
);

export const AudienceSegmentChangeExtension = workflowsPlugin.provide(
  AUDIENCE_SEGMENT_CHANGE_EXTENSION,
);

export const SampleSizeChangeExtension = workflowsPlugin.provide(
  SAMPLE_SIZE_CHANGE_EXTENSION,
);

export const WorkflowStateChangeExtension = workflowsPlugin.provide(
  STATE_CHANGE_EXTENSION,
);
